(() => {
  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, {once: true});
    } else {
      callback();
    }
  };

  const optionValue = (variantOption, index) =>
    variantOption?.dataset[`option${index + 1}`] || '';

  const initGallery = (root) => {
    root.querySelectorAll('[data-reference-gallery]').forEach((gallery) => {
      const slides = [...gallery.querySelectorAll('[data-reference-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-reference-thumb]')];
      const previous = gallery.querySelector('[data-reference-previous]');
      const next = gallery.querySelector('[data-reference-next]');
      const current = gallery.querySelector('[data-reference-current]');
      const stage = gallery.querySelector('.reference-gallery__stage');
      if (slides.length < 2) return;

      let activeIndex = 0;
      let touchStartX = 0;

      const show = (requestedIndex, {centerThumb = true} = {}) => {
        const nextIndex = (requestedIndex + slides.length) % slides.length;

        slides.forEach((slide, index) => {
          const active = index === nextIndex;
          if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });

        thumbs.forEach((thumb, index) => {
          const active = index === nextIndex;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-selected', String(active));
          thumb.tabIndex = active ? 0 : -1;
        });

        activeIndex = nextIndex;
        if (current) current.textContent = String(nextIndex + 1);
        if (centerThumb && thumbs[nextIndex]) {
          thumbs[nextIndex].scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
        }
      };

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => show(index));
        thumb.addEventListener('keydown', (event) => {
          let targetIndex;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            targetIndex = (index + 1) % thumbs.length;
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            targetIndex = (index - 1 + thumbs.length) % thumbs.length;
          } else if (event.key === 'Home') {
            targetIndex = 0;
          } else if (event.key === 'End') {
            targetIndex = thumbs.length - 1;
          } else {
            return;
          }

          event.preventDefault();
          show(targetIndex);
          thumbs[targetIndex].focus();
        });
      });

      previous?.addEventListener('click', () => show(activeIndex - 1));
      next?.addEventListener('click', () => show(activeIndex + 1));

      stage?.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0]?.clientX || 0;
      }, {passive: true});

      stage?.addEventListener('touchend', (event) => {
        if (event.target.closest('video, button')) return;
        const touchEndX = event.changedTouches[0]?.clientX || 0;
        const distance = touchEndX - touchStartX;
        if (Math.abs(distance) < 48) return;
        show(activeIndex + (distance < 0 ? 1 : -1));
      }, {passive: true});

      show(0, {centerThumb: false});
    });
  };

  const initMobileBuy = (root, form, panel, mobileButton) => {
    const mobileViewport = window.matchMedia('(max-width: 989px)');
    const footer = document.querySelector('footer');
    let scheduled = false;

    const updateVisibility = () => {
      scheduled = false;
      if (!mobileViewport.matches) {
        root.classList.remove('is-mobile-buy-visible');
        return;
      }

      const formRect = form.getBoundingClientRect();
      const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
      root.classList.toggle('is-mobile-buy-visible', formRect.bottom < 76 && !footerVisible);
    };

    const scheduleUpdate = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(updateVisibility);
    };

    window.addEventListener('scroll', scheduleUpdate, {passive: true});
    window.addEventListener('resize', scheduleUpdate, {passive: true});
    mobileViewport.addEventListener?.('change', scheduleUpdate);

    mobileButton?.addEventListener('click', () => {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      form.scrollIntoView({behavior: reduceMotion ? 'auto' : 'smooth', block: 'start'});
      const focusTarget = form.querySelector('select:not([hidden]), input:not([type="hidden"]), button:not(:disabled)');
      window.setTimeout(() => focusTarget?.focus({preventScroll: true}), reduceMotion ? 0 : 450);
    });

    panel.addEventListener('focusin', scheduleUpdate);
    scheduleUpdate();
  };

  const initProduct = (root) => {
    initGallery(root);

    const panel = root.querySelector('[data-parity-panel]');
    const form = root.querySelector('[data-parity-form]');
    const select = form?.querySelector('[data-parity-variant-select]');
    if (!panel || !form || !select) return;

    const price = panel.querySelector('[data-parity-price]');
    const compare = panel.querySelector('[data-parity-compare]');
    const submit = form.querySelector('[data-parity-submit]');
    const mobilePrice = root.querySelector('[data-parity-mobile-price]');
    const mobileButton = root.querySelector('[data-parity-scroll]');
    const summary = form.querySelector('[data-parity-selection-summary]');
    const mainImage = root.querySelector('[data-parity-main-media] img');
    const groups = [...form.querySelectorAll('[data-parity-option-group]')];
    const variantOptions = [...select.options];
    const fixedPack = form.querySelector('.parity-option-group--fixed button')?.textContent?.trim();

    const selectedValues = () =>
      groups.map((group) =>
        group.querySelector('[data-parity-option].is-selected')?.dataset.optionValue || '',
      );

    const findVariant = (values) =>
      variantOptions.find((variantOption) =>
        values.every((value, index) => !value || optionValue(variantOption, index) === value),
      );

    const updateOptionGroups = (variantOption) => {
      groups.forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const value = optionValue(variantOption, index);
        const readout = group.querySelector('[data-parity-option-readout]');
        if (readout) readout.textContent = value;
        group.querySelectorAll('[data-parity-option]').forEach((button) => {
          const selected = button.dataset.optionValue === value;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-checked', String(selected));
          button.tabIndex = selected ? 0 : -1;
        });
      });
    };

    const update = ({syncUrl = false} = {}) => {
      const variantOption = select.selectedOptions[0];
      if (!variantOption) return;

      const available = variantOption.dataset.available === 'true';
      const currentPrice = variantOption.dataset.price || '';
      const comparePrice = variantOption.dataset.compare || '';

      if (price) price.textContent = currentPrice;
      if (mobilePrice) mobilePrice.textContent = currentPrice;
      if (compare) {
        compare.textContent = comparePrice;
        compare.hidden = !comparePrice;
      }
      if (submit) {
        submit.disabled = !available;
        submit.textContent = available
          ? submit.dataset.availableText
          : submit.dataset.soldOutText;
      }
      if (mobileButton) {
        mobileButton.disabled = !available;
        mobileButton.textContent = available
          ? mobileButton.dataset.availableText
          : mobileButton.dataset.soldOutText;
      }

      updateOptionGroups(variantOption);

      if (summary) {
        const variantTitle = variantOption.textContent.trim();
        summary.textContent = fixedPack ? `${variantTitle} · ${fixedPack}` : variantTitle;
      }

      const image = variantOption.dataset.image;
      if (mainImage && image) {
        mainImage.src = image;
        mainImage.removeAttribute('srcset');
      }

      if (syncUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantOption.value);
        window.history.replaceState({}, '', url);
      }
    };

    groups.forEach((group) => {
      group.addEventListener('click', (event) => {
        const button = event.target.closest('[data-parity-option]');
        if (!button || button.disabled) return;
        const index = Number(button.dataset.optionIndex);
        const values = selectedValues();
        values[index] = button.dataset.optionValue;
        const matchedVariant = findVariant(values);
        if (!matchedVariant) return;
        select.value = matchedVariant.value;
        update({syncUrl: true});
      });

      group.addEventListener('keydown', (event) => {
        const button = event.target.closest('[data-parity-option]');
        if (!button) return;
        const buttons = [...group.querySelectorAll('[data-parity-option]:not(:disabled)')];
        const currentIndex = buttons.indexOf(button);
        if (currentIndex < 0) return;

        let targetIndex;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          targetIndex = (currentIndex + 1) % buttons.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          targetIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        } else if (event.key === 'Home') {
          targetIndex = 0;
        } else if (event.key === 'End') {
          targetIndex = buttons.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        buttons[targetIndex].focus();
        buttons[targetIndex].click();
      });
    });

    select.addEventListener('change', () => update({syncUrl: true}));
    initMobileBuy(root, form, panel, mobileButton);
    update();
  };

  onReady(() => {
    document.querySelectorAll('[data-parity-product]').forEach(initProduct);
  });
})();
